import { useContext, useMemo } from 'react';

import axios, { AxiosInstance } from 'axios';
import { useLocation } from 'wouter';

import { cwaUrl } from '/configuration';
import { ContentLocation } from '/types';

import { AppShellContext } from './contexts';

// 30x codes are processed by the browser and are not exposed to
// javascript. The rarely used HTTP code 409 is our server
// instruction for the client to redirect. See details here:
// https://github.com/axios/axios/issues/932#issuecomment-307390761
export const CLIENT_SIDE_REDIRECT_HTTP_CODE = 409;

export const buildArticleApiRelativeUrl = ({
  publishStatus,
  contentType,
  slugOrId,
}: ContentLocation): string => {
  const parts = ['/api/content'];
  if (publishStatus === 'preview') {
    parts.push('preview');
  } else if (publishStatus == 'long_term_preview') {
    parts.push('long-term-preview');
  }
  parts.push(contentType, slugOrId);
  return parts.join('/');
};

type useCwaAxiosFn = (setIsLoading?: (state: boolean) => void) => AxiosInstance;

export const useCwaAxios: useCwaAxiosFn = (setIsLoading): AxiosInstance => {
  const { portalJwt } = useContext(AppShellContext);
  const setLocation = useLocation()[1];

  return useMemo(() => {
    const instance = axios.create({
      baseURL: cwaUrl,
      headers: portalJwt ? { Authorization: `Bearer ${portalJwt}` } : {},
    });

    // Add a request interceptor
    instance.interceptors.request.use(
      function (config) {
        setIsLoading && setIsLoading(true);
        return config;
      },
      function (error) {
        setIsLoading && setIsLoading(false);
        return Promise.reject(error);
      },
    );

    // Add a response interceptor
    instance.interceptors.response.use(
      function (response) {
        setIsLoading && setIsLoading(false);
        return response;
      },
      function (error) {
        setIsLoading && setIsLoading(false);
        const httpCode = error?.response?.status;
        if (httpCode === CLIENT_SIDE_REDIRECT_HTTP_CODE) {
          setLocation(
            error.response.data.location.replace('/api/content', '') +
              // Don't lose the URL params on redirect
              `${window.location.search}${window.location.hash}`,
          );
          return Promise.resolve(error);
        }
        return Promise.reject(error);
      },
    );

    return instance;
  }, [portalJwt, setIsLoading, setLocation]);
};
